import { createSlice } from "@reduxjs/toolkit";

// Local storage key for chat persistence (user-specific)
const CHAT_STORAGE_KEY = "fasfas_chat_history";

// Get user-specific storage key based on userId
const getUserChatKey = (userId) => {
  if (!userId || userId.startsWith("guest_")) {
    return `${CHAT_STORAGE_KEY}_guest`;
  }
  return `${CHAT_STORAGE_KEY}_${userId}`;
};

// Load chat history from local storage for specific user
const loadChatFromStorage = (userId) => {
  try {
    const storageKey = getUserChatKey(userId);
    const savedChat = localStorage.getItem(storageKey);
    if (savedChat) {
      const parsed = JSON.parse(savedChat);
      // Validate the structure
      if (Array.isArray(parsed.messages)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error("Error loading chat from storage:", error);
  }
  return {
    messages: [],
    sessionId: null
  };
};

// Save chat history to local storage for specific user
const saveChatToStorage = (chatData, userId) => {
  try {
    const storageKey = getUserChatKey(userId);
    console.log('[CHAT DEBUG] Saving chat to:', storageKey, 'Messages count:', chatData.messages.length);
    localStorage.setItem(storageKey, JSON.stringify(chatData));
  } catch (error) {
    console.error("Error saving chat to storage:", error);
  }
};

// Get guest user ID
const getGuestUserId = () => {
  return "guest_" + Date.now();
};

const userId = getGuestUserId();
const chatData = loadChatFromStorage(userId);

// Load chat active state from localStorage
const loadChatActiveState = () => {
  try {
    return localStorage.getItem('fasfas_chat_active') === 'true';
  } catch (error) {
    return false;
  }
};

const initialState = {
  messages: chatData.messages,
  isLoading: false,
  isActive: loadChatActiveState(), // Restore chat state from localStorage
  sessionId: chatData.sessionId || null,
  error: null,
  userId: userId,
  chatOpenCount: 0, // <-- Add this line
};

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setMessages: (state, action) => {
      state.messages = action.payload;
      saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
      // Do NOT trim here! Only save and set isActive.
      saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
      state.isActive = true;
      try {
        localStorage.setItem('fasfas_chat_active', 'true');
      } catch (error) {
        console.error('Error saving chat active state:', error);
      }
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsActive: (state, action) => {
      state.isActive = action.payload;
      // Always reload chat history for the current userId when chat is opened
      if (action.payload === true) {
        const chatData = loadChatFromStorage(state.userId);
        state.messages = chatData.messages;
        state.sessionId = chatData.sessionId;
      }
      // Save chat active state to localStorage
      try {
        localStorage.setItem('fasfas_chat_active', action.payload.toString());
      } catch (error) {
        console.error('Error saving chat active state:', error);
      }
    },
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
      saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
    },
    clearChat: (state) => {
      state.messages = [];
      state.sessionId = null;
      state.error = null;

      // Only clear CURRENT user's chat history, not other users'
      try {
        const storageKey = getUserChatKey(state.userId);
        console.log('[CHAT DEBUG] Clearing only current user:', storageKey);
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Error clearing chat localStorage:', error);
      }
    },
    clearChatState: (state) => {
      // Clear chat error only (not messages or isActive) - keep chat persistent across sessions
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    initializeWelcomeMessage: (state) => {
      if (state.messages.length === 0) {
        const welcomeMessage = {
          id: "welcome_" + Date.now(),
          content: "Hi! I'm your FasFas gaming assistant. How can I help you today? You can ask me about:\n\n• Your account balance\n\n• Game recommendations\n\n• Bonuses and promotions\n\n• How to deposit or withdraw\n\n• Responsible gaming features\n\nWhat would you like to know?",
          isUser: false,
          timestamp: Date.now(),
          type: "welcome"
        };
        state.messages.push(welcomeMessage);
        saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
      }
    },
    addUserMessage: (state, action) => {
      const userMessage = {
        id: Date.now() + "_user",
        content: action.payload.message,
        isUser: true,
        timestamp: Date.now(),
        type: "text"
      };
      state.messages.push(userMessage);
      saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
    },
    updateSessionId: (state, action) => {
      if (action.payload) {
        state.sessionId = action.payload;
        saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);
      }
    },
    reloadChatHistory: (state) => {
      // Reload chat history from localStorage for current user (don't change userId)
      const chatData = loadChatFromStorage(state.userId);
      state.messages = chatData.messages;
      state.sessionId = chatData.sessionId;
      // Restore chat active state from localStorage
      try {
        const isActive = localStorage.getItem('fasfas_chat_active') === 'true';
        state.isActive = isActive;
      } catch (error) {
        console.error('Error loading chat active state:', error);
      }
    },
    updateUserIdAndReload: (state, action) => {
      // Update userId with the provided value (phone number from Redux state)
      const newUserId = action.payload;
      console.log('[CHAT DEBUG] updateUserIdAndReload called with:', newUserId);
      console.log('[CHAT DEBUG] Current userId:', state.userId);

      if (newUserId && !newUserId.startsWith("guest_")) {
        // Save current chat state before switching user
        saveChatToStorage({ messages: state.messages, sessionId: state.sessionId }, state.userId);

        // Update to new user
        state.userId = newUserId;
        // Mark chat as active when switching user
        state.isActive = true;
        try {
          localStorage.setItem('fasfas_chat_active', 'true');
        } catch (error) {
          console.error('Error saving chat active state:', error);
        }

        // Load chat history for this specific user
        const chatData = loadChatFromStorage(newUserId);
        console.log('[CHAT DEBUG] Loaded chat data for user:', newUserId, 'Messages count:', chatData.messages.length);
        state.messages = chatData.messages;
        state.sessionId = chatData.sessionId;

        // Maintain chat active state
        try {
          const isActive = localStorage.getItem('fasfas_chat_active') === 'true';
          state.isActive = isActive;
        } catch (error) {
          console.error('Error loading chat active state:', error);
        }
      }
    },
    initializeChat: (state, action) => {
      const userId = action.payload;
      if (userId && !userId.startsWith("guest_")) {
        console.log('[CHAT DEBUG] Initializing chat for user:', userId);
        state.userId = userId;

        // Load chat history for this user
        const chatData = loadChatFromStorage(userId);
        state.messages = chatData.messages;
        state.sessionId = chatData.sessionId;

        // Restore chat active state
        try {
          const isActive = localStorage.getItem('fasfas_chat_active') === 'true';
          state.isActive = isActive;
        } catch (error) {
          console.error('Error loading chat active state:', error);
        }

        console.log('[CHAT DEBUG] Chat initialized - Messages:', chatData.messages.length, 'Active:', state.isActive);
      }
    },
    incrementChatOpenCount: (state) => {
      state.chatOpenCount += 1;
    }
  }
});

export const {
  setMessages,
  addMessage,
  setIsLoading,
  setIsActive,
  setSessionId,
  clearChat,
  clearChatState,
  clearError,
  initializeWelcomeMessage,
  addUserMessage,
  updateSessionId,
  reloadChatHistory,
  updateUserIdAndReload,
  initializeChat,
  incrementChatOpenCount
} = chatbotSlice.actions;

// Selectors
export const selectChatMessages = (state) => state.chatbot.messages;
export const selectChatLoading = (state) => state.chatbot.isLoading;
export const selectChatActive = (state) => state.chatbot.isActive;
export const selectChatError = (state) => state.chatbot.error;
export const selectUserId = (state) => state.chatbot.userId;
export const selectUserPhone = (state) => {
  // Get userId from userDetails (from API result.userId)
  const userDetails = state?.signIn?.userDetails;
  if (userDetails?.userId) {
    console.log('[CHAT DEBUG] Got userId from userDetails:', userDetails.userId);
    return String(userDetails.userId);
  }

  // Fallback to playerDetails (phone string during login)
  const playerDetails = state?.signIn?.playerDetails;
  if (playerDetails && typeof playerDetails === 'string') {
    console.log('[CHAT DEBUG] Got phone from playerDetails:', playerDetails);
    return playerDetails;
  }

  console.log('[CHAT DEBUG] No userId/phone found in state');
  return null;
};

export const selectChatOpenCount = (state) => state.chatbot.chatOpenCount; // <-- Add this selector

export default chatbotSlice.reducer;